import { getRoutes } from '@js/configs/routes.js'
import { JourneyCreator } from '@js/utilities/journey'
import { createRouter, createWebHistory } from 'vue-router'
import { setGlobalData } from '@js/utilities/globalContent.js'

// lodash components
import find from 'lodash/find'
import indexOf from 'lodash/indexOf'
import replace from 'lodash/replace'
import split from 'lodash/split'
import startsWith from 'lodash/startsWith'

function until(conditionFunction, time = 50) {
    const poll = resolve => {
        if (conditionFunction()) resolve()
        else setTimeout(_ => poll(resolve), time)
    }

    return new Promise(poll)
}

export default function initRouter(store, i18n, translationsPromise, emitter) {
    /**
     * Router
     */
    const router = createRouter({
        history: createWebHistory(),
        routes: [...getRoutes(i18n)],
        scrollBehavior(to, from, savedPosition) {
            return { top: 0 }
        },
    })

    router.beforeResolve(async to => {
        // dynamic routes not loaded yet - wait for them
        if (to.name == '404' && startsWith(to.path, `/${JourneyCreator.getApplyPath()}`)) {
            await until(_ => store.getters.applyRoutesLoaded)

            const hasRoute = router.getRoutes().find(route => {
                return route.path === to.path
            })

            if (hasRoute) {
                return to.fullPath
            }
        }
    })

    let wasLastPageApply = false
    router.beforeEach(async (to, from, next) => {
        const partnerUrl = store?.state?.global?.data?.theme_settings?.partner_url
        if (to.name === 'home' && partnerUrl) {
            next({ path: partnerUrl })
            return
        }

        // If vue router does not detect a hash in the URL
        // but there clearly is one, manually handle it
        if (!to.hash && indexOf(to.fullPath, '#') >= 0) {
            const pathParts = split(to.fullPath, '#')

            next({ path: pathParts[0], hash: `#${replace(pathParts[1], /%20/g, ' ')}`, query: to.query })
            return
        }

        const isNowApply = startsWith(to.name, 'apply')
        let refreshData = true
        if (isNowApply) {
            if (wasLastPageApply) {
                refreshData = false
            }
        }

        wasLastPageApply = isNowApply

        if (refreshData) {
            // wait for all the async data synchronously - we don't want page navigation to overlap
            await setGlobalData(to, store, i18n, translationsPromise, router, emitter)
        }

        if (isNowApply) {
            await until(_ => store.getters.applyRoutesLoaded)

            const toStep = store.getters.stepViaRouteName(to.name)
            if (!toStep || !toStep.completed) {
                const firstStepRoute = router.getRoutes().find(route => {
                    return route.name === store.getters.getFirstStep.route.name
                })

                // make sure current url is valid part of current journey.
                // If not, redirect to beginning (this clips the ability to go back past what is allowed)
                if (toStep !== store.getters.firstUncompletedStep && to.name !== firstStepRoute.name) {
                    router.replace({ name: firstStepRoute.name, query: to.query })
                    next(false)
                    return
                }
            }
        }

        if (to.name === 'result') {
            const previousRoutes = from.matched
            const fromApply =
                find(previousRoutes, { path: '/apply/terms' }) ||
                find(previousRoutes, { path: '/result/:reference' }) ||
                find(previousRoutes, { path: '/wez-pozyczke/uzyskaj-moje-wyniki' })

            if (fromApply) {
                to.params.isDirectRoute = false
            } else {
                to.params.isDirectRoute = true
            }
        }

        next()
    })

    router.afterEach((to, from) => {
        if (to.path !== from.path) {
            emitter.emit('insertOnetrust')
        }
    })

    return router
}
